/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
// Removed unused CustomSelect import
import AccountSelection from '../components/AccountSelection';
import SymbolSelection from '../components/SymbolSelection';
import TradeControls from '../components/TradeControls';
import RiskManagement from '../components/RiskManagement';
import TradeExecution from '../components/TradeExecution';
import PartialProfitTargets from '../components/PartialProfitTargets';
import TradeCalculations from '../components/TradeCalculations';
import JournalModal from '../components/JournalModal';
import { Accordion, AccordionItem } from '../components/ui/accordion';
import TradingChart from '../components/TradingChart';
import { usePriceFeed } from '../context/PriceFeedContext';
import { useAccountData } from '../context/AccountDataProvider';
import { getSymbolInfo } from '../api/api';
import { setDigitsFor } from '../utils/symbolDigitsStore';
import { useTradeState } from '../hooks/useTradeState';
import { useTrade } from '../context/TradeProvider';

const timeframes = [
    { label: '1M', value: 'M1' },
    { label: '5M', value: 'M5' },
    { label: '15M', value: 'M15' },
    { label: '30M', value: 'M30' },
    { label: '1H', value: 'H1' },
    { label: '4H', value: 'H4' },
    { label: '1D', value: 'D1' },
    { label: '1W', value: 'W1' },
    { label: '1MN', value: 'MN1' },
];

const LiveTradeView = ({ watchlist: initialWatchlist }) => {
    const { isLoggingEnabled, toggleLogging } = usePriceFeed();
    const { accountId, symbol, timeframe, setTradeContext } = useTrade();
    const {
        tradeDirection,
        setTradeDirection,
        orderType,
        setOrderType,
        stopLossType,
        setStopLossType,
        riskPercent,
        setRiskPercent,
        atrMultiplier,
        setAtrMultiplier,
        tpMode,
        setTpMode,
        rrRatio,
        setRrRatio,
        takeProfitValue,
        setTakeProfitValue,
    } = useTradeState();
    const {
        balance: accountBalance,
        equity: accountEquity,
        freeMargin,
        cashAvailable,
        currency: accountCurrency,
        openPositions,
        connectedAccountId,
        connect,
    } = useAccountData();
    const { bid, ask } = usePriceFeed();
    const [pipSize, setPipSize] = useState(null);
    const [tickSize, setTickSize] = useState(null);
    const [contractSize, setContractSize] = useState(null);
    const [symbolCurrency, setSymbolCurrency] = useState(null);
    const [priceConversionRate, setPriceConversionRate] = useState(null);
    const [digits, setDigits] = useState(undefined);
    const [lotStep, setLotStep] = useState(0.01);
    const [minLot, setMinLot] = useState(undefined);
    const [maxLot, setMaxLot] = useState(undefined);
    const [lotSize, setLotSize] = useState('N/A');
    const [stopLossPrice, setStopLossPrice] = useState('N/A');
    const [stopLossDistance, setStopLossDistance] = useState('N/A');
    const [finalStopLossDistance, setFinalStopLossDistance] = useState(0);
    const [projectedProfit, setProjectedProfit] = useState(0);
    const [projectedLoss, setProjectedLoss] = useState(0);
    const [takeProfitPrice, setTakeProfitPrice] = useState('');
    const [reason, setReason] = useState('');
    const [limitEntryPrice, setLimitEntryPrice] = useState('');
    const [profitMode, setProfitMode] = useState('single');
    const [targets, setTargets] = useState([
        { rank: 1, share: 0.5, tp_type: 'RR', rr: 1.5, price: '', atr_multiplier: 1 },
        { rank: 2, share: 0.5, tp_type: 'RR', rr: 3.0, price: '', atr_multiplier: 1 },
    ]);
    const [watchlist, setWatchlist] = useState(initialWatchlist || []);
    const [executedTradeId, setExecutedTradeId] = useState(null);
    const [showJournalModal, setShowJournalModal] = useState(false);
    const [showRecentTrades, setShowRecentTrades] = useState(false);

    const resetTradeTerminal = useCallback(() => {
        setLotSize('N/A');
        setStopLossPrice('N/A');
        setStopLossDistance('N/A');
        setFinalStopLossDistance(0);
        setProjectedProfit(0);
        setProjectedLoss(0);
        setTakeProfitPrice('');
        setLimitEntryPrice('');
        setReason('');
    }, []);

    useEffect(() => {
        setWatchlist(initialWatchlist || []);
    }, [initialWatchlist]);

    useEffect(() => {
        if (accountId) {
            connect(accountId);
        }
        // No disconnect on cleanup — connection persists across page navigation
    }, [accountId, connect]);

    useEffect(() => {
        // This effect now only fetches symbol info when a *valid* symbol is set in the context.
        // The validation itself is handled in SymbolSelection.
        if (accountId && symbol) {
            getSymbolInfo(accountId, symbol)
                .then((symbolInfo) => {
                    setPipSize(symbolInfo.pip_size);
                    setTickSize(symbolInfo.tick_size);
                    setContractSize(symbolInfo.contract_size);
                    setSymbolCurrency(symbolInfo.currency ?? null);
                    const conversionRate = Number(symbolInfo.price_conversion_rate);
                    setPriceConversionRate(Number.isFinite(conversionRate) ? conversionRate : null);
                    // Lot step and bounds for proper lot rounding
                    const step = Number(symbolInfo.lot_step ?? symbolInfo.volume_step ?? 0.01) || 0.01;
                    setLotStep(step);
                    setMinLot(typeof symbolInfo.min_lot === 'number' ? symbolInfo.min_lot : (typeof symbolInfo.min_volume === 'number' ? symbolInfo.min_volume : undefined));
                    setMaxLot(typeof symbolInfo.max_lot === 'number' ? symbolInfo.max_lot : (typeof symbolInfo.max_volume === 'number' ? symbolInfo.max_volume : undefined));
                    if (typeof symbolInfo.digits === 'number') {
                        setDigits(symbolInfo.digits);
                        setDigitsFor(accountId, symbol, symbolInfo.digits);
                    } else {
                        setDigits(undefined);
                    }
                    resetTradeTerminal();
                })
                .catch((error) => {
                    // It's unlikely to fail here now, but good to have a fallback.
                    console.error("Error fetching info for a supposedly valid symbol:", error);
                });
        } else {
            // If there's no symbol, clear the info.
            setPipSize(null);
            setTickSize(null);
            setContractSize(null);
            setSymbolCurrency(null);
            setPriceConversionRate(null);
            setDigits(undefined);
            setLotStep(0.01);
            setMinLot(undefined);
            setMaxLot(undefined);
        }
    }, [accountId, symbol, resetTradeTerminal]);

    useEffect(() => {
        setFinalStopLossDistance(stopLossDistance);
    }, [stopLossDistance]);

    const handleTargetsCalculated = (calculatedTargets) => {
        const newTargets = targets.map(originalTarget => {
            const foundCalculated = calculatedTargets.find(ct => ct.rank === originalTarget.rank);
            if (foundCalculated && foundCalculated.calculatedPrice !== undefined) {
                return { ...originalTarget, price: foundCalculated.calculatedPrice };
            }
            return originalTarget;
        });
        setTargets(newTargets);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-4 p-2 sm:p-4 min-h-screen bg-background">
            {/* Left Column: Chart */}
            <div className="lg:col-span-8 flex flex-col h-[50vh] lg:h-full order-1 lg:order-1">
                <Card className="flex-grow flex flex-col">
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-6">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full">
                            <div className="flex items-center gap-1 sm:gap-2">
                                <span className="text-sm sm:text-lg font-semibold">Account:</span>
                                <AccountSelection
                                    selectedAccount={accountId}
                                    setSelectedAccount={(newAccountId) => setTradeContext({ accountId: newAccountId })}
                                    setRiskPercentFromSettings={setRiskPercent}
                                    displayMode="compact"
                                />
                            </div>
                            <div className="flex items-center">
                                <SymbolSelection
                                    selectedSymbol={symbol}
                                    setSelectedSymbol={(newSymbol) => setTradeContext({ symbol: newSymbol })}
                                    watchlist={watchlist}
                                    accountId={accountId}
                                />
                            </div>
                            {/* Removed header timeframe select; moved into chart overlay */}
                            <button
                                onClick={toggleLogging}
                                className={`ml-auto p-1.5 sm:p-2 rounded text-xs sm:text-sm ${isLoggingEnabled ? 'bg-green-500' : 'bg-red-500'} text-white`}
                            >
                                {isLoggingEnabled ? 'Log ON' : 'Log OFF'}
                            </button>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col p-1 sm:p-2">
                        <TradingChart
                            key={`${symbol}-${timeframe}`}
                            selectedAccount={accountId}
                            selectedSymbol={symbol}
                            selectedTimeframe={timeframe}
                            stopLossPrice={stopLossPrice}
                            takeProfitPrice={takeProfitPrice}
                            openPositions={String(connectedAccountId ?? '') === String(accountId ?? '') ? openPositions : []}
                            tickSize={tickSize}
                            digits={digits}
                            pipSize={pipSize}
                            // Enable overlay timeframe selector
                            showTimeframeOverlay={true}
                            timeframeOptions={timeframes}
                            onTimeframeChange={(newTimeframe) => setTradeContext({ timeframe: newTimeframe })}
                            showRecentTrades={showRecentTrades}
                            onToggleRecentTrades={() => setShowRecentTrades(v => !v)}
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Right Column: Trade Terminal */}
            <div className="lg:col-span-4 flex flex-col h-auto lg:h-full order-2 lg:order-2">
                <Card className="flex-grow flex flex-col">
                    <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="text-base sm:text-xl">Trade Terminal</CardTitle>
                        <div className="mt-2 text-sm sm:text-xl font-bold text-textPrimary grid grid-cols-2 sm:block gap-2">
                            <p>Balance: ${typeof accountBalance === 'number' ? accountBalance.toFixed(2) : "..."}</p>
                            <p>Equity: ${typeof accountEquity === 'number' ? accountEquity.toFixed(2) : "..."}</p>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-grow overflow-y-auto space-y-3 sm:space-y-4 p-3 sm:p-6">
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Trade Controls</h3>
                            <TradeControls
                                tradeDirection={tradeDirection}
                                setTradeDirection={setTradeDirection}
                                selectedAccount={accountId}
                                selectedSymbol={symbol}
                                orderType={orderType}
                                setOrderType={setOrderType}
                                limitEntryPrice={limitEntryPrice}
                                setLimitEntryPrice={setLimitEntryPrice}
                            />
                        </div>
                        <Accordion>
                            <AccordionItem title="Symbol Info">
                                <div className="space-y-1">
                                    <div className="flex flex-wrap gap-2 sm:gap-4 text-xs">
                                        <p>Pip: {pipSize}</p>
                                        <p>Tick: {tickSize}</p>
                                        <p>Contract: {contractSize}</p>
                                    </div>
                                </div>
                            </AccordionItem>
                        </Accordion>
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Risk Management</h3>
                            <RiskManagement
                                stopLossType={stopLossType}
                                setStopLossType={setStopLossType}
                                stopLossDistance={stopLossDistance}
                                setStopLossDistance={setStopLossDistance}
                                stopLossPrice={stopLossPrice}
                                setStopLossPrice={setStopLossPrice}
                                riskPercent={riskPercent}
                                setRiskPercent={setRiskPercent}
                                tradeDirection={tradeDirection}
                                bidPrice={bid}
                                askPrice={ask}
                                pipSize={pipSize}
                                tickSize={tickSize}
                                contractSize={contractSize}
                                lotStep={lotStep}
                                minLot={minLot}
                                maxLot={maxLot}
                                setLotSize={setLotSize}
                                accountEquity={accountEquity}
                                freeMargin={freeMargin}
                                cashAvailable={cashAvailable}
                                accountCurrency={accountCurrency}
                                symbolCurrency={symbolCurrency}
                                priceConversionRate={priceConversionRate}
                                takeProfitValue={takeProfitValue}
                                setTakeProfitValue={setTakeProfitValue}
                                rrRatio={rrRatio}
                                setRrRatio={setRrRatio}
                                setProjectedProfit={setProjectedProfit}
                                projectedProfit={projectedProfit}
                                setProjectedLoss={setProjectedLoss}
                                projectedLoss={projectedLoss}
                                tpMode={tpMode}
                                setTpMode={setTpMode}
                                takeProfitPrice={takeProfitPrice}
                                setTakeProfitPrice={setTakeProfitPrice}
                                selectedSymbol={symbol}
                                selectedTimeframe={timeframe}
                                atrMultiplier={atrMultiplier}
                                setAtrMultiplier={setAtrMultiplier}
                                profitMode={profitMode}
                                setProfitMode={setProfitMode}
                                targets={targets}
                                onTargetsCalculated={handleTargetsCalculated}
                                orderType={orderType}
                                limitEntryPrice={limitEntryPrice}
                            />
                        </div>
                        {profitMode === 'multi' && (
                            <div>
                                <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Partial Profit Targets</h3>
                                <PartialProfitTargets
                                    accountId={accountId}
                                    targets={targets}
                                    setTargets={setTargets}
                                />
                            </div>
                        )}
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Trade Calculations</h3>
                            <TradeCalculations
                                lotSize={lotSize}
                                projectedLoss={projectedLoss}
                                projectedProfit={projectedProfit}
                                profitTarget={takeProfitPrice}
                                stopLossPrice={stopLossPrice}
                                stopLossDistance={stopLossDistance}
                            />
                        </div>
                        <div className="mt-3 sm:mt-4">
                            <TradeExecution
                                selectedAccount={accountId}
                                selectedSymbol={symbol}
                                tradeDirection={tradeDirection}
                                lotSize={lotSize}
                                bidPrice={bid}
                                askPrice={ask}
                                stopLossDistance={finalStopLossDistance}
                                pipSize={pipSize}
                                riskPercent={riskPercent}
                                rrRatio={rrRatio}
                                projectedProfit={projectedProfit}
                                projectedLoss={projectedLoss}
                                takeProfitValue={tpMode === "price" ? takeProfitPrice : takeProfitValue}
                                tpMode={tpMode}
                                takeProfitPrice={takeProfitPrice}
                                reason={reason}
                                orderType={orderType}
                                limitEntryPrice={limitEntryPrice}
                                profitMode={profitMode}
                                targets={targets}
                                lotStep={lotStep}
                                minLot={minLot}
                                maxLot={maxLot}
                                setExecutedTradeId={setExecutedTradeId}
                                setShowJournalModal={setShowJournalModal}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
            {showJournalModal && (
                <JournalModal
                    show={showJournalModal}
                    initialSnapshotSymbol={symbol}
                    onClose={() => {
                        setShowJournalModal(false);
                        setExecutedTradeId(null); // Clear the trade ID when modal is closed
                    }}
                    tradeId={executedTradeId}
                />
            )}
        </div>
    );
};

const LiveTradePage = ({ watchlist }) => (
    <LiveTradeView watchlist={watchlist} />
);

export default LiveTradePage;
